import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectContext";
import { BrandMark, BRAND } from "../components/Brand";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "../components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft, Plus, PencilSimple, Trash, Buildings, FolderOpen,
  CaretDown, CaretRight, ArrowSquareOut, Users as UsersIcon, Tag,
} from "@phosphor-icons/react";

const STATUS_BADGE = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-neutral-100 text-neutral-600 border-neutral-200",
};

export default function Setup() {
  const { user } = useAuth();
  const { reload } = useProjects();

  const [customers, setCustomers] = useState([]);
  const [projectsByCustomer, setProjectsByCustomer] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [membersByProject, setMembersByProject] = useState({});

  // Modal state
  const [custForm, setCustForm] = useState(null); // {open, initial}
  const [projForm, setProjForm] = useState(null); // {open, initial, customer_id}
  const [assignFor, setAssignFor] = useState(null); // project
  const [confirmDel, setConfirmDel] = useState(null); // {kind, item}

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, p, u] = await Promise.all([
        api.get("/customers"),
        api.get("/projects"),
        api.get("/users"),
      ]);
      setCustomers(c.data.customers || []);
      const byCust = {};
      (p.data.projects || []).forEach((pr) => {
        byCust[pr.customer_id] = byCust[pr.customer_id] || [];
        byCust[pr.customer_id].push(pr);
      });
      setProjectsByCustomer(byCust);
      setAllUsers(u.data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load setup data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openCustomerForm = (initial = null) => setCustForm({ open: true, initial });
  const openProjectForm = (customer_id, initial = null) => setProjForm({ open: true, customer_id, initial });

  const saveCustomer = async (data) => {
    try {
      if (custForm.initial) await api.patch(`/customers/${custForm.initial.id}`, data);
      else await api.post("/customers", data);
      toast.success("Customer saved");
      setCustForm(null);
      loadAll();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveProject = async (data) => {
    try {
      const payload = { ...data, customer_id: projForm.customer_id };
      if (projForm.initial) await api.patch(`/projects/${projForm.initial.id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Project saved");
      setProjForm(null);
      loadAll();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    const { kind, item } = confirmDel;
    try {
      if (kind === "customer") await api.delete(`/customers/${item.id}`);
      else await api.delete(`/projects/${item.id}`);
      toast.success(`${kind === "customer" ? "Customer" : "Project"} deleted`);
      setConfirmDel(null);
      loadAll();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const loadMembers = async (projectId) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/members`);
      setMembersByProject((m) => ({ ...m, [projectId]: data.members || [] }));
    } catch { /* ignore */ }
  };

  const saveAssignment = async (userIds) => {
    try {
      await api.post(`/projects/${assignFor.id}/assign`, { user_ids: userIds });
      toast.success("Access updated");
      loadMembers(assignFor.id);
      setAssignFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="setup-page">
      <header className="h-16 border-b border-[#E5E5E5] bg-white flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              PROJECT TRACKER
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Setup</span>
          </div>
        </div>
        <Link to="/">
          <Button variant="outline" size="sm" data-testid="back-to-dashboard-btn" className="h-9">
            <ArrowLeft size={16} weight="bold" className="mr-2" />
            Back to Tracker
          </Button>
        </Link>
      </header>

      <div className="flex-1 p-6 max-w-6xl w-full mx-auto space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
              Workspace Setup
            </p>
            <h1 className="text-3xl font-black tracking-tight mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
              Customers & Projects
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              Define customers, add projects underneath, and grant user access per project. Each project has its own task tracker.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/setup/object-types">
              <Button variant="outline" data-testid="setup-object-types-link" className="h-10">
                <Tag size={16} weight="bold" className="mr-2" />
                Object Types
              </Button>
            </Link>
            <Button
              onClick={() => openCustomerForm()}
              data-testid="new-customer-btn"
              className="bg-[#387EE6] hover:bg-[#2563EB] text-white"
            >
              <Plus size={16} weight="bold" className="mr-2" />
              New Customer
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 py-10 text-center">Loading…</div>
        ) : customers.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-lg p-12 text-center">
            <Buildings size={40} weight="thin" className="text-neutral-400 mx-auto mb-3" />
            <p className="text-neutral-600 text-sm mb-4">No customers yet. Add your first customer to get started.</p>
            <Button onClick={() => openCustomerForm()} className="bg-[#387EE6] hover:bg-[#2563EB] text-white">
              <Plus size={16} weight="bold" className="mr-2" /> New Customer
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {customers.map((c) => {
              const isOpen = expanded.has(c.id);
              const projects = projectsByCustomer[c.id] || [];
              return (
                <div key={c.id} className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden" data-testid={`customer-${c.id}`}>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggle(c.id)}
                      className="h-6 w-6 flex items-center justify-center text-neutral-500 hover:text-neutral-900"
                      data-testid={`toggle-customer-${c.id}`}
                    >
                      {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                    </button>
                    <Buildings size={18} weight="bold" className="text-neutral-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-neutral-900">{c.name}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_BADGE[c.status]}`}>
                          {c.status}
                        </span>
                        <span className="text-xs text-neutral-500">{projects.length} project{projects.length === 1 ? "" : "s"}</span>
                      </div>
                      {c.description ? (
                        <p className="text-xs text-neutral-500 mt-0.5 truncate">{c.description}</p>
                      ) : null}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openProjectForm(c.id)} data-testid={`new-project-btn-${c.id}`} className="h-8">
                      <Plus size={12} weight="bold" className="mr-1" /> Project
                    </Button>
                    <button onClick={() => openCustomerForm(c)} className="h-8 w-8 flex items-center justify-center rounded hover:bg-neutral-100 text-neutral-700" data-testid={`edit-customer-btn-${c.id}`}>
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button onClick={() => setConfirmDel({ kind: "customer", item: c })} className="h-8 w-8 flex items-center justify-center rounded hover:bg-red-50 text-red-600" data-testid={`delete-customer-btn-${c.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                  {isOpen ? (
                    <div className="border-t border-[#E5E5E5] bg-[#FAFAFA] p-4 space-y-2">
                      {projects.length === 0 ? (
                        <p className="text-xs text-neutral-500 italic">No projects yet.</p>
                      ) : projects.map((p) => (
                        <div key={p.id} className="bg-white border border-[#E5E5E5] rounded px-3 py-2.5 flex items-center gap-3" data-testid={`project-${p.id}`}>
                          <FolderOpen size={16} weight="bold" className="text-sky-700" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-neutral-900 text-sm">{p.name}</span>
                              {p.code ? <span className="text-[10px] font-mono bg-neutral-100 px-1.5 py-0.5 rounded">{p.code}</span> : null}
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_BADGE[p.status]}`}>{p.status}</span>
                              {p.start_date ? <span className="text-[10px] text-neutral-500">{p.start_date} → {p.end_date || "…"}</span> : null}
                            </div>
                            {p.description ? <p className="text-xs text-neutral-500 mt-0.5 truncate">{p.description}</p> : null}
                          </div>
                          <Button size="sm" variant="outline" onClick={() => { setAssignFor(p); loadMembers(p.id); }} data-testid={`assign-btn-${p.id}`} className="h-8">
                            <UsersIcon size={12} weight="bold" className="mr-1" /> Access
                          </Button>
                          <Link to={`/?project_id=${p.id}`}>
                            <Button size="sm" variant="outline" data-testid={`open-tracker-btn-${p.id}`} className="h-8">
                              <ArrowSquareOut size={12} weight="bold" className="mr-1" /> Open tracker
                            </Button>
                          </Link>
                          <button onClick={() => openProjectForm(c.id, p)} className="h-8 w-8 flex items-center justify-center rounded hover:bg-neutral-100 text-neutral-700" data-testid={`edit-project-btn-${p.id}`}>
                            <PencilSimple size={14} weight="bold" />
                          </button>
                          <button onClick={() => setConfirmDel({ kind: "project", item: p })} className="h-8 w-8 flex items-center justify-center rounded hover:bg-red-50 text-red-600" data-testid={`delete-project-btn-${p.id}`}>
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Customer form */}
      <EntityForm
        open={!!custForm?.open}
        onOpenChange={(v) => !v && setCustForm(null)}
        title={custForm?.initial ? "Edit Customer" : "New Customer"}
        initial={custForm?.initial}
        fields={[
          { key: "name", label: "Name", required: true },
          { key: "description", label: "Description", type: "textarea" },
          { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"], defaultValue: "Active" },
        ]}
        onSubmit={saveCustomer}
        testPrefix="customer-form"
      />

      {/* Project form */}
      <EntityForm
        open={!!projForm?.open}
        onOpenChange={(v) => !v && setProjForm(null)}
        title={projForm?.initial ? "Edit Project" : "New Project"}
        initial={projForm?.initial}
        fields={[
          { key: "name", label: "Name", required: true },
          { key: "code", label: "Code" },
          { key: "description", label: "Description", type: "textarea" },
          { key: "start_date", label: "Start Date", type: "date" },
          { key: "end_date", label: "End Date", type: "date" },
          { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"], defaultValue: "Active" },
        ]}
        onSubmit={saveProject}
        testPrefix="project-form"
      />

      {/* Assign users */}
      <AssignSheet
        open={!!assignFor}
        onOpenChange={(v) => !v && setAssignFor(null)}
        project={assignFor}
        allUsers={allUsers}
        currentMembers={assignFor ? (membersByProject[assignFor.id] || []) : []}
        onSubmit={saveAssignment}
      />

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent data-testid="setup-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDel?.kind}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-semibold">{confirmDel?.item?.name}</span>.
              {confirmDel?.kind === "customer" ? " The customer must have no projects." : " The project must have no active tasks."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="setup-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EntityForm({ open, onOpenChange, title, initial, fields, onSubmit, testPrefix }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init = {};
    fields.forEach((f) => {
      init[f.key] = initial?.[f.key] ?? f.defaultValue ?? "";
    });
    setData(init);
  }, [open, initial]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSubmit(data); } finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-white flex flex-col h-full" data-testid={`${testPrefix}-sheet`}>
        <form onSubmit={submit} className="flex flex-col h-full">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E5E5] shrink-0">
            <SheetTitle className="text-2xl font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</SheetTitle>
            <SheetDescription className="text-neutral-500">Fill in the details below.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{f.label}{f.required ? " *" : ""}</Label>
                {f.type === "textarea" ? (
                  <Textarea rows={3} required={f.required} value={data[f.key] || ""} onChange={(e) => setData({ ...data, [f.key]: e.target.value })} data-testid={`${testPrefix}-${f.key}`} />
                ) : f.type === "select" ? (
                  <Select value={data[f.key] || ""} onValueChange={(v) => setData({ ...data, [f.key]: v })}>
                    <SelectTrigger data-testid={`${testPrefix}-${f.key}`} className="h-10"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type || "text"} required={f.required} value={data[f.key] || ""} onChange={(e) => setData({ ...data, [f.key]: e.target.value })} data-testid={`${testPrefix}-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <SheetFooter className="px-6 py-4 border-t border-[#E5E5E5] shrink-0 flex flex-row justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid={`${testPrefix}-submit-btn`} className="bg-[#387EE6] hover:bg-[#2563EB] text-white">
              {saving ? "Saving..." : "Save"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function AssignSheet({ open, onOpenChange, project, allUsers, currentMembers, onSubmit }) {
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (open) setSelected(new Set((currentMembers || []).map((m) => m.id)));
  }, [open, currentMembers]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const eligible = useMemo(() => (allUsers || []).filter((u) => u.role !== "admin"), [allUsers]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-white flex flex-col h-full" data-testid="assign-sheet">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E5E5] shrink-0">
          <SheetTitle className="text-2xl font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            Project Access
          </SheetTitle>
          <SheetDescription className="text-neutral-500">
            Choose editors/viewers who can access <span className="font-medium">{project?.name}</span>. Admins always have access.
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {eligible.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">No editors or viewers yet. Add users in the Manage Users page.</p>
          ) : eligible.map((u) => (
            <label key={u.id} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-neutral-50 cursor-pointer" data-testid={`assign-user-${u.id}`}>
              <input
                type="checkbox"
                checked={selected.has(u.id)}
                onChange={() => toggle(u.id)}
                className="cursor-pointer accent-neutral-900"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900">{u.name || u.email.split("@")[0]}</p>
                <p className="text-xs text-neutral-500 font-mono">{u.email}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-neutral-500">{u.role}</span>
            </label>
          ))}
        </div>
        <SheetFooter className="px-6 py-4 border-t border-[#E5E5E5] shrink-0 flex flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            type="button"
            onClick={() => onSubmit(Array.from(selected))}
            data-testid="assign-save-btn"
            className="bg-[#387EE6] hover:bg-[#2563EB] text-white"
          >
            Save Access
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
